import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
    IRetryPolicy
} from '@microsoft/signalr';
import { fromEvent, merge } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ConfigService } from '../config/config.service';
import { LoggingService } from '../shared/logging/logging.service';

export interface SignalRConfig {
    hubUrl: string;
    automaticReconnect?: boolean | number[] | IRetryPolicy | { enabled: boolean; retryDelays?: number[] };
    logLevel?: LogLevel;
    accessTokenFactory?: () => string | Promise<string>;
    withCredentials?: boolean;
    connectionTimeout?: number;
    serverTimeoutInMilliseconds?: number;
    maxReconnectAttempts?: number;
    healthCheckInterval?: number;
    enableConnectionHealthCheck?: boolean;
}

export interface SignalRConnectionState {
    state: HubConnectionState;
    isConnected: boolean;
    isConnecting: boolean;
    isDisconnected: boolean;
    isReconnecting: boolean;
    error?: Error;
    lastConnectionAttempt?: Date;
    reconnectAttempts?: number;
    healthCheckStatus?: 'healthy' | 'unhealthy' | 'unknown';
}

@Injectable({
    providedIn: 'root'
})
export class SignalRService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly configService = inject(ConfigService);
    private readonly toastr = inject(ToastrService);
    private readonly loggingService = inject(LoggingService);

    private hubConnection: HubConnection | null = null;
    private config: SignalRConfig | null = null;
    private healthCheckInterval: number | null = null;
    private reconnectTimeout: number | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private lastConnectionAttempt: Date | null = null;
    private isManualDisconnect = false; // Track if disconnect was intentional

    // Signals for reactive state management
    private readonly connectionStateSignal = signal<HubConnectionState>(HubConnectionState.Disconnected);
    private readonly connectionErrorSignal = signal<Error | null>(null);
    private readonly isReconnectingSignal = signal<boolean>(false);
    private readonly healthCheckStatusSignal = signal<'healthy' | 'unhealthy' | 'unknown'>('unknown');

    // Public readonly signals
    readonly connectionState = this.connectionStateSignal.asReadonly();
    readonly connectionError = this.connectionErrorSignal.asReadonly();
    readonly isReconnecting = this.isReconnectingSignal.asReadonly();

    // Computed signals for convenience
    readonly isConnected = computed(() => this.connectionStateSignal() === HubConnectionState.Connected);
    readonly isConnecting = computed(() => this.connectionStateSignal() === HubConnectionState.Connecting);
    readonly isDisconnected = computed(() => this.connectionStateSignal() === HubConnectionState.Disconnected);

    // Connection state info computed signal
    readonly connectionInfo = computed<SignalRConnectionState>(() => ({
        state: this.connectionStateSignal(),
        isConnected: this.isConnected(),
        isConnecting: this.isConnecting(),
        isDisconnected: this.isDisconnected(),
        isReconnecting: this.isReconnecting(),
        error: this.connectionErrorSignal() || undefined,
        lastConnectionAttempt: this.lastConnectionAttempt || undefined,
        reconnectAttempts: this.reconnectAttempts,
        healthCheckStatus: this.healthCheckStatusSignal()
    }));

    constructor() {
        // Setup cleanup on service destruction
        this.destroyRef.onDestroy(() => {
            this.cleanup();
        });

        // Listen for browser events that might affect connection
        this.setupBrowserEventHandlers();
    }

    /**
     * Initialize the SignalR connection with configuration
     */
    initialize(config: SignalRConfig): void {
        if (this.hubConnection) {
            this.loggingService.warn('SignalRService', 'SignalR connection already initialized. Call disconnect() first to reinitialize.');
            return;
        }

        this.config = config;
        this.buildConnection();
    }

    /**
     * Initialize the SignalR connection with configuration from config service
     */
    initializeWithEnvironment(overrides?: Partial<SignalRConfig>): void {
        if (!this.configService.config?.signalRHubUrl) {
            throw new Error('SignalR hub URL not configured in config service');
        }

        const signalRConfig = this.configService.config.signalR;
        const logLevel = this.mapLogLevel(signalRConfig.logLevel);

        const config: SignalRConfig = {
            hubUrl: this.configService.config.signalRHubUrl,
            automaticReconnect: signalRConfig.automaticReconnect.enabled
                ? signalRConfig.automaticReconnect.retryDelays
                : false,
            logLevel,
            withCredentials: signalRConfig.withCredentials,
            connectionTimeout: signalRConfig.connectionTimeout,
            serverTimeoutInMilliseconds: 120000, // 2 minutes - longer than server keep-alive interval (30s)
            maxReconnectAttempts: signalRConfig.maxReconnectAttempts,
            healthCheckInterval: signalRConfig.healthCheckInterval,
            enableConnectionHealthCheck: signalRConfig.enableConnectionHealthCheck,
            ...overrides
        };

        this.initialize(config);
    }

    /**
     * Initialize the SignalR connection specifically for golf scoring with configuration from config service
     */
    initializeForGolfScoring(authTokenFactory: () => string | Promise<string>, overrides?: Partial<SignalRConfig>): void {
        if (!this.configService.config?.signalRHubUrl) {
            throw new Error('SignalR hub URL not configured in config service');
        }

        const signalRConfig = this.configService.config.signalR;
        const golfConfig = signalRConfig.golfScoring;
        const logLevel = this.mapLogLevel(golfConfig.logLevel);

        const config: SignalRConfig = {
            hubUrl: this.configService.config.signalRHubUrl,
            automaticReconnect: golfConfig.retryDelays, // Use golf-specific retry delays
            logLevel,
            accessTokenFactory: authTokenFactory,
            withCredentials: signalRConfig.withCredentials,
            connectionTimeout: signalRConfig.connectionTimeout,
            serverTimeoutInMilliseconds: 120000, // 2 minutes - longer than server keep-alive interval (30s)
            maxReconnectAttempts: golfConfig.maxReconnectAttempts,
            healthCheckInterval: golfConfig.healthCheckInterval,
            enableConnectionHealthCheck: signalRConfig.enableConnectionHealthCheck,
            ...overrides
        };

        this.initialize(config);
    }

    /**
     * Start the SignalR connection
     */
    async connect(): Promise<void> {
        if (!this.hubConnection) {
            throw new Error('SignalR connection not initialized. Call initialize() first.');
        }

        if (this.hubConnection.state === HubConnectionState.Connected) {
            this.loggingService.warn('SignalRService', 'SignalR is already connected');
            return;
        }

        if (this.hubConnection.state === HubConnectionState.Connecting) {
            this.loggingService.warn('SignalRService', 'SignalR connection is already in progress');
            return;
        }

        try {
            this.lastConnectionAttempt = new Date();
            this.connectionStateSignal.set(HubConnectionState.Connecting);
            this.connectionErrorSignal.set(null);
            this.healthCheckStatusSignal.set('unknown');

            this.loggingService.debug('SignalRService', 'Starting connection');
            await this.hubConnection.start();

            // Reset reconnect attempts on successful connection
            this.reconnectAttempts = 0;
            this.connectionStateSignal.set(HubConnectionState.Connected);
            this.healthCheckStatusSignal.set('healthy');

            this.loggingService.info('SignalRService', 'Connected successfully with transport: ' + this.getCurrentTransport());
            this.loggingService.debug('SignalRService', 'Connection state: ' + this.hubConnection.state);

            this.loggingService.info('SignalRService', 'SignalR connected successfully');
            this.toastr.info('SignalR connected successfully', 'Connected');

            // Start health check if enabled
            this.startHealthCheck();

        } catch (error) {
            const err = error instanceof Error ? error : new Error('Unknown connection error');
            this.connectionErrorSignal.set(err);
            this.connectionStateSignal.set(HubConnectionState.Disconnected);
            this.healthCheckStatusSignal.set('unhealthy');

            this.loggingService.error('SignalRService', 'SignalR connection failed: ' + err.message, err);
            this.loggingService.error('SignalRService', 'SignalR connection error details', err);
            this.loggingService.error('SignalRService', 'SignalR hub URL: ' + this.config?.hubUrl);

            // Show notification for initial connection failure
            if (navigator.onLine) {
                this.toastr.error('Failed to connect to SignalR - service may be offline', 'Connection Failed');
            } else {
                this.toastr.error('Failed to connect to SignalR - you are offline', 'Connection Failed');
            }

            // Attempt automatic reconnection if configured
            this.scheduleReconnect(err);

            throw err;
        }
    }

    /**
     * Stop the SignalR connection
     */
    async disconnect(): Promise<void> {
        if (!this.hubConnection) {
            return;
        }

        try {
            this.isManualDisconnect = true; // Set flag to indicate manual disconnect

            // Stop health check
            this.stopHealthCheck('manual disconnect');

            await this.hubConnection.stop();
            this.connectionStateSignal.set(HubConnectionState.Disconnected);
            this.healthCheckStatusSignal.set('unknown');
            this.loggingService.info('SignalRService', 'SignalR connection stopped');
        } catch (error) {
            this.loggingService.error('SignalRService', 'Error stopping SignalR connection', error);
        } finally {
            this.cleanup();
        }
    }

    /**
     * Register a handler for a hub method
     */
    on<T = any>(methodName: string, handler: (...args: any[]) => void): void {
        if (!this.hubConnection) {
            throw new Error('SignalR connection not initialized. Call initialize() first.');
        }

        this.hubConnection.on(methodName, handler);
    }

    /**
     * Unregister a handler for a hub method
     */
    off(methodName: string, handler?: (...args: any[]) => void): void {
        if (!this.hubConnection) {
            return;
        }

        if (handler) {
            this.hubConnection.off(methodName, handler);
        } else {
            this.hubConnection.off(methodName);
        }
    }

    /**
     * Invoke a hub method
     */
    async invoke<T = any>(methodName: string, ...args: any[]): Promise<T> {
        if (!this.hubConnection) {
            throw new Error('SignalR connection not initialized. Call initialize() first.');
        }

        if (this.hubConnection.state !== HubConnectionState.Connected) {
            throw new Error('SignalR connection is not in Connected state');
        }

        try {
            return await this.hubConnection.invoke<T>(methodName, ...args);
        } catch (error) {
            this.loggingService.error('SignalRService', `Error invoking SignalR method '${methodName}'`, error);
            throw error;
        }
    }

    /**
     * Send a message to the hub (fire and forget)
     */
    async send(methodName: string, ...args: any[]): Promise<void> {
        if (!this.hubConnection) {
            throw new Error('SignalR connection not initialized. Call initialize() first.');
        }

        if (this.hubConnection.state !== HubConnectionState.Connected) {
            throw new Error('SignalR connection is not in Connected state');
        }

        try {
            await this.hubConnection.send(methodName, ...args);
        } catch (error) {
            this.loggingService.error('SignalRService', `Error sending SignalR message '${methodName}'`, error);
            throw error;
        }
    }

    /**
     * Get the current connection state
     */
    getConnectionState(): HubConnectionState {
        return this.hubConnection?.state ?? HubConnectionState.Disconnected;
    }

    /**
     * Get the connection ID if connected
     */
    getConnectionId(): string | null {
        return this.hubConnection?.connectionId ?? null;
    }

    /**
     * Get current transport type being used
     */
    getCurrentTransport(): string | null {
        if (!this.hubConnection) {
            return null;
        }

        // Access the transport information from the connection
        const transport = (this.hubConnection as any).transport;
        if (!transport) {
            // Try alternative property names that might exist
            const connection = this.hubConnection as any;
            const alternativeTransport = connection._transport || connection.transport || connection._connection?.transport;

            if (alternativeTransport) {
                const altName = alternativeTransport.constructor?.name;
                if (altName) {
                    return this.mapTransportName(altName);
                }
            }

            // If we can't find transport but are connected, make an educated guess based on configuration
            if (this.hubConnection.state === HubConnectionState.Connected) {
                const hubUrl = this.config?.hubUrl;
                if (hubUrl?.startsWith('http://')) {
                    // HTTP environment, likely Server-Sent Events since we configured it that way
                    return 'ServerSentEventsTransport';
                } else if (hubUrl?.startsWith('https://')) {
                    // HTTPS environment, likely WebSockets
                    return 'WebSocketTransport';
                }
            }

            return null;
        }

        const transportName = transport.constructor?.name || 'Unknown';
        return this.mapTransportName(transportName);
    }

    /**
     * Map technical transport names to consistent internal names
     */
    private mapTransportName(transportName: string): string {
        switch (transportName) {
            case 'WebSocketTransport':
            case 'WebSocketsTransport':
                return 'WebSocketTransport';
            case 'ServerSentEventsTransport':
            case 'EventSourceTransport':
                return 'ServerSentEventsTransport';
            case 'LongPollingTransport':
                return 'LongPollingTransport';
            default:
                return transportName;
        }
    }

    /**
     * Check if connection is using WebSockets (optimal transport)
     */
    isUsingWebSockets(): boolean {
        const transport = this.getCurrentTransport();
        return transport === 'WebSocketTransport';
    }

    /**
     * Check if connection is using Server-Sent Events (efficient transport)
     */
    isUsingServerSentEvents(): boolean {
        const transport = this.getCurrentTransport();
        return transport === 'ServerSentEventsTransport';
    }

    /**
     * Check if connection is using Long Polling (fallback transport)
     */
    isUsingLongPolling(): boolean {
        const transport = this.getCurrentTransport();
        return transport === 'LongPollingTransport';
    }

    /**
     * Check if running in local development environment
     */
    isLocalDevelopment(): boolean {
        if (!this.configService.config?.signalRHubUrl) {
            return false;
        }
        return this.configService.config.signalRHubUrl.includes('localhost');
    }

    /**
     * Join a SignalR group for receiving group-specific messages
     */
    async joinGroup(groupName: string): Promise<void> {
        if (!this.hubConnection) {
            throw new Error('SignalR connection not initialized. Call initialize() first.');
        }

        if (this.hubConnection.state !== HubConnectionState.Connected) {
            throw new Error('SignalR connection is not in Connected state');
        }

        try {
            await this.hubConnection.invoke('JoinGroup', groupName);
            this.loggingService.info('SignalRService', `Successfully joined group: ${groupName}`);
        } catch (error) {
            this.loggingService.error('SignalRService', `Error joining group '${groupName}'`, error);
            throw error;
        }
    }

    /**
     * Leave a SignalR group to stop receiving group-specific messages
     */
    async leaveGroup(groupName: string): Promise<void> {
        if (!this.hubConnection) {
            throw new Error('SignalR connection not initialized. Call initialize() first.');
        }

        if (this.hubConnection.state !== HubConnectionState.Connected) {
            throw new Error('SignalR connection is not in Connected state');
        }

        try {
            await this.hubConnection.invoke('LeaveGroup', groupName);
            this.loggingService.info('SignalRService', `Successfully left group: ${groupName}`);
        } catch (error) {
            this.loggingService.error('SignalRService', `Error leaving group '${groupName}'`, error);
            throw error;
        }
    }

    /**
     * Join a player-specific group for receiving player-related events
     */
    async joinPlayerGroup(playerId: string): Promise<void> {
        if (!playerId) {
            throw new Error('Player ID is required to join player group');
        }

        try {
            await this.hubConnection!.invoke('JoinPlayerGroup', playerId);
            this.loggingService.info('SignalRService', `Successfully joined player group for player: ${playerId}`);
        } catch (error) {
            this.loggingService.error('SignalRService', `Error joining player group for player '${playerId}'`, error);
            throw error;
        }
    }

    /**
     * Leave a player-specific group to stop receiving player-related events
     */
    async leavePlayerGroup(playerId: string): Promise<void> {
        if (!playerId) {
            throw new Error('Player ID is required to leave player group');
        }

        try {
            await this.hubConnection!.invoke('LeavePlayerGroup', playerId);
            this.loggingService.info('SignalRService', `Successfully left player group for player: ${playerId}`);
        } catch (error) {
            this.loggingService.error('SignalRService', `Error leaving player group for player '${playerId}'`, error);
            throw error;
        }
    }

    /**
     * Enhanced restart method with better error handling
     */
    async restart(): Promise<void> {
        if (!this.config) {
            throw new Error('No configuration available for restart');
        }

        this.loggingService.info('SignalRService', 'Restarting SignalR connection...');

        try {
            await this.disconnect();

            // Small delay to ensure cleanup is complete
            await new Promise(resolve => setTimeout(resolve, 100));

            this.buildConnection();
            await this.connect();

            this.loggingService.info('SignalRService', 'SignalR connection restarted successfully');
        } catch (error) {
            this.loggingService.error('SignalRService', 'Failed to restart SignalR connection', error);
            throw error;
        }
    }

    /**
     * Test WebSocket connectivity separately to diagnose issues
     */
    async testWebSocketConnection(): Promise<{ success: boolean; error?: string }> {
        if (!this.configService.config?.signalRHubUrl) {
            return { success: false, error: 'SignalR hub URL not configured' };
        }

        try {
            // Create a test connection with automatic transport negotiation
            const testBuilder = new HubConnectionBuilder()
                .withUrl(this.configService.config.signalRHubUrl, {
                    accessTokenFactory: this.config?.accessTokenFactory
                })
                .configureLogging(LogLevel.Debug);

            const testConnection = testBuilder.build();

            await testConnection.start();
            await testConnection.stop();

            return { success: true };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.loggingService.error('SignalRService', 'WebSocket test failed', error);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Get the configured hub URL
     */
    getHubUrl(): string | null {
        return this.configService.config?.signalRHubUrl || null;
    }

    private buildConnection(): void {
        if (!this.config) {
            throw new Error('SignalR configuration is required');
        }

        // Store max reconnect attempts from config
        this.maxReconnectAttempts = this.config.maxReconnectAttempts || 10;

        this.loggingService.debug('SignalRService', 'Building connection with default transport negotiation');

        const builder = new HubConnectionBuilder()
            .withUrl(this.config.hubUrl, {
                accessTokenFactory: this.config.accessTokenFactory,
                withCredentials: this.config.withCredentials ?? false,
                timeout: this.config.connectionTimeout ?? 30000
            })
            .configureLogging(this.config.logLevel ?? LogLevel.Information);

        // Configure server timeout - how long client waits for server messages before timing out
        const serverTimeout = this.config.serverTimeoutInMilliseconds ?? 120000; // Default 2 minutes
        builder.withServerTimeout(serverTimeout);

        // Configure automatic reconnect with enhanced retry policy
        if (this.config.automaticReconnect !== false) {
            if (this.config.automaticReconnect === true) {
                // Default exponential backoff: 0, 2, 10, 30 seconds
                builder.withAutomaticReconnect([0, 2000, 10000, 30000]);
            } else if (this.config.automaticReconnect && typeof this.config.automaticReconnect === 'object') {
                // Handle object format with enabled flag and retryDelays
                if ('enabled' in this.config.automaticReconnect && this.config.automaticReconnect.enabled) {
                    const retryDelays = (this.config.automaticReconnect as any).retryDelays || [0, 2000, 10000, 30000];
                    builder.withAutomaticReconnect(retryDelays);
                } else if (Array.isArray(this.config.automaticReconnect)) {
                    // Handle array format
                    builder.withAutomaticReconnect(this.config.automaticReconnect);
                } else if ('nextRetryDelayInMilliseconds' in this.config.automaticReconnect) {
                    // Handle IRetryPolicy format
                    builder.withAutomaticReconnect(this.config.automaticReconnect);
                }
            }
        }

        this.hubConnection = builder.build();
        this.setupConnectionEventHandlers();
    }

    private setupConnectionEventHandlers(): void {
        if (!this.hubConnection) {
            return;
        }

        // Connection state change handlers
        this.hubConnection.onclose((error) => {
            this.connectionStateSignal.set(HubConnectionState.Disconnected);
            this.stopHealthCheck('connection closed');

            if (error) {
                this.connectionErrorSignal.set(error);
                this.healthCheckStatusSignal.set('unhealthy');
                this.loggingService.error('SignalRService', 'SignalR connection closed with error: ' + error.message, error);

                // Only show error notification if not a manual disconnect
                if (!this.isManualDisconnect) {
                    this.toastr.error('SignalR connection lost', 'Disconnected');
                }

                // Only attempt manual reconnection if automatic reconnection is disabled
                // Otherwise, rely on SignalR's built-in automatic reconnection
                if (!this.hasAutomaticReconnectEnabled()) {
                    this.scheduleReconnect(error);
                }
            } else {
                this.connectionErrorSignal.set(null);
                this.healthCheckStatusSignal.set('unknown');
                this.loggingService.info('SignalRService', 'SignalR connection closed cleanly');

                // Only show info notification if not a manual disconnect
                if (!this.isManualDisconnect) {
                    this.toastr.info('SignalR disconnected', 'Disconnected');
                }
            }

            // Reset manual disconnect flag
            this.isManualDisconnect = false;
            this.isReconnectingSignal.set(false);
        });

        this.hubConnection.onreconnecting((error) => {
            this.connectionStateSignal.set(HubConnectionState.Reconnecting);
            this.isReconnectingSignal.set(true);
            this.reconnectAttempts++;
            this.lastConnectionAttempt = new Date();

            this.loggingService.info('SignalRService', `SignalR reconnecting (attempt ${this.reconnectAttempts})...`);

            if (error) {
                this.loggingService.warn('SignalRService', 'SignalR reconnecting due to error: ' + error.message, error);
                this.connectionErrorSignal.set(error);
                this.healthCheckStatusSignal.set('unhealthy');
            }
        });

        this.hubConnection.onreconnected((connectionId) => {
            this.connectionStateSignal.set(HubConnectionState.Connected);
            this.connectionErrorSignal.set(null);
            this.isReconnectingSignal.set(false);
            this.healthCheckStatusSignal.set('healthy');

            // Reset reconnect attempts on successful reconnection
            this.reconnectAttempts = 0;

            this.loggingService.info('SignalRService', `SignalR reconnected successfully with connection ID: ${connectionId}`);

            // Restart health check
            this.startHealthCheck();
        });
    }

    private setupBrowserEventHandlers(): void {
        // Handle browser online/offline events
        merge(
            fromEvent(window, 'online'),
            fromEvent(window, 'offline')
        ).pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((event) => {
            if (event.type === 'online') {
                this.loggingService.info('SignalRService', 'Browser came online');

                // Give a small delay to ensure network is stable
                setTimeout(() => {
                    if (this.hubConnection && this.isDisconnected()) {
                        this.loggingService.info('SignalRService', 'Browser online: attempting to reconnect SignalR');
                        this.toastr.info('Connection restored, attempting to reconnect SignalR', 'Online');
                        this.connect().catch(error => {
                            this.loggingService.error('SignalRService', 'Failed to reconnect after coming online', error);
                        });
                    } else if (this.hubConnection && this.isConnected()) {
                        this.loggingService.info('SignalRService', 'Browser online: SignalR already connected');
                        this.toastr.success('Connection restored, SignalR is connected', 'Online');
                    }
                }, 1000); // 1 second delay
            } else if (event.type === 'offline') {
                this.loggingService.info('SignalRService', 'Browser went offline');
                if (this.hubConnection && this.isConnected()) {
                    this.toastr.warning('Connection lost - SignalR will attempt to reconnect when online', 'Offline');
                } else {
                    this.toastr.warning('Connection lost - you are now offline', 'Offline');
                }
            }
        });

        // Handle page visibility changes
        fromEvent(document, 'visibilitychange').pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            if (!document.hidden && this.hubConnection && this.isDisconnected()) {
                this.loggingService.info('SignalRService', 'Page became visible, checking SignalR connection');
                this.connect().catch(error => {
                    this.loggingService.error('SignalRService', 'Failed to reconnect after page became visible', error);
                });
            }
        });
    }

    /**
     * Check if automatic reconnection is enabled in the configuration
     */
    private hasAutomaticReconnectEnabled(): boolean {
        if (!this.config || this.config.automaticReconnect === false) {
            return false;
        }

        if (this.config.automaticReconnect === true) {
            return true;
        }

        if (typeof this.config.automaticReconnect === 'object') {
            if ('enabled' in this.config.automaticReconnect) {
                return (this.config.automaticReconnect as any).enabled === true;
            }
            // If it's an array or IRetryPolicy, it means automatic reconnection is enabled
            return true;
        }

        return false;
    }

    /**
     * Schedule automatic reconnection with exponential backoff
     */
    private scheduleReconnect(error: Error): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.loggingService.error('SignalRService', `Maximum reconnection attempts (${this.maxReconnectAttempts}) reached. Giving up.`);
            this.healthCheckStatusSignal.set('unhealthy');
            this.toastr.error(`Failed to reconnect after ${this.maxReconnectAttempts} attempts`, 'Connection Failed');
            return;
        }

        // Don't schedule reconnect if already reconnecting via built-in mechanism
        if (this.isReconnecting()) {
            this.loggingService.debug('SignalRService', 'Already reconnecting via built-in mechanism, skipping manual reconnect');
            return;
        }

        // Don't schedule reconnect if we're offline
        if (!navigator.onLine) {
            this.loggingService.debug('SignalRService', 'Device is offline, skipping manual reconnection attempt');
            return;
        }

        // Calculate delay with exponential backoff
        const baseDelay = 1000; // 1 second
        const maxDelay = 30000; // 30 seconds
        const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts), maxDelay);

        this.loggingService.debug('SignalRService', `Scheduling manual reconnection attempt ${this.reconnectAttempts + 1} in ${delay}ms...`);

        this.reconnectTimeout = window.setTimeout(async () => {
            this.reconnectTimeout = null;

            // Double-check we're still disconnected and not already reconnecting
            if (this.hubConnection && this.isDisconnected() && !this.isReconnecting()) {
                try {
                    this.loggingService.debug('SignalRService', `Attempting manual reconnection (attempt ${this.reconnectAttempts + 1})...`);
                    await this.connect();
                } catch (reconnectError) {
                    this.loggingService.error('SignalRService', 'Manual reconnection failed', reconnectError);
                    // The failed connection will trigger another scheduleReconnect via the onclose handler
                }
            } else {
                this.loggingService.debug('SignalRService', 'Skipping manual reconnection - connection state changed');
            }
        }, delay);
    }

    /**
     * Start health check monitoring
     */
    private startHealthCheck(): void {
        if (!this.config?.enableConnectionHealthCheck) {
            return;
        }

        this.stopHealthCheck('restarting health check'); // Clear any existing interval

        const interval = this.config.healthCheckInterval || 30000; // Default 30 seconds

        this.healthCheckInterval = window.setInterval(async () => {
            await this.performHealthCheck();
        }, interval) as unknown as number;

        this.loggingService.debug('SignalRService', `Health check monitoring started (interval: ${interval}ms)`);
    }

    /**
     * Stop health check monitoring
     */
    private stopHealthCheck(reason?: string): void {
        if (this.healthCheckInterval) {
            window.clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
            this.loggingService.debug('SignalRService', `Health check monitoring stopped${reason ? ` (${reason})` : ''}`);
        }
    }

    /**
     * Perform connection health check
     */
    private async performHealthCheck(): Promise<void> {
        // Skip health check if we're already reconnecting or disconnected
        if (!this.hubConnection ||
            this.hubConnection.state === HubConnectionState.Reconnecting ||
            this.hubConnection.state === HubConnectionState.Disconnected) {
            this.healthCheckStatusSignal.set('unhealthy');
            return;
        }

        // Only perform health check if connected
        if (this.hubConnection.state !== HubConnectionState.Connected) {
            this.healthCheckStatusSignal.set('unhealthy');
            return;
        }

        try {
            // Simple health check - just verify connection ID exists
            if (this.hubConnection.connectionId) {
                this.healthCheckStatusSignal.set('healthy');
            } else {
                this.healthCheckStatusSignal.set('unhealthy');
                this.loggingService.warn('SignalRService', 'Health check failed: No connection ID available');

                // Don't restart - let the built-in reconnection handle it
                // Only log the issue for debugging purposes
            }
        } catch (error) {
            this.healthCheckStatusSignal.set('unhealthy');
            this.loggingService.error('SignalRService', 'Health check failed', error);

            // Don't restart the connection - let SignalR's built-in reconnection handle it
            // Restarting here can interfere with automatic reconnection attempts
        }
    }

    /**
     * Cleanup resources
     */
    private cleanup(): void {
        this.stopHealthCheck('service cleanup');

        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        this.hubConnection = null;
        this.config = null;
        this.connectionErrorSignal.set(null);
        this.reconnectAttempts = 0;
        this.lastConnectionAttempt = null;
        this.healthCheckStatusSignal.set('unknown');
        this.isManualDisconnect = false;
    }

    /**
     * Get detailed connection statistics
     */
    getConnectionStats(): {
        state: string;
        connectionId: string | null;
        transport: string | null;
        reconnectAttempts: number;
        lastConnectionAttempt: Date | null;
        healthStatus: string;
        isHealthy: boolean;
    } {
        return {
            state: HubConnectionState[this.connectionStateSignal()],
            connectionId: this.getConnectionId(),
            transport: this.getCurrentTransport(),
            reconnectAttempts: this.reconnectAttempts,
            lastConnectionAttempt: this.lastConnectionAttempt,
            healthStatus: this.healthCheckStatusSignal(),
            isHealthy: this.healthCheckStatusSignal() === 'healthy'
        };
    }

    /**
     * Force immediate reconnection attempt
     */
    async forceReconnect(): Promise<void> {
        this.loggingService.info('SignalRService', 'Force reconnecting...');

        if (this.isConnected()) {
            this.loggingService.info('SignalRService', 'Force reconnecting (currently connected)...');
            await this.restart();
        } else {
            this.loggingService.info('SignalRService', 'Force reconnecting (currently disconnected)...');
            this.reconnectAttempts = 0; // Reset attempts for manual reconnection

            // Clear any existing scheduled reconnections
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }

            try {
                await this.connect();
                this.toastr.success('Force reconnection successful', 'Reconnected');
            } catch (error) {
                this.loggingService.error('SignalRService', 'Force reconnection failed', error);
                this.toastr.error('Force reconnection failed', 'Connection Error');
                throw error;
            }
        }
    }

    /**
     * Map string log level to LogLevel enum
     */
    private mapLogLevel(logLevel: string): LogLevel {
        switch (logLevel.toLowerCase()) {
            case 'none':
                return LogLevel.None;
            case 'critical':
                return LogLevel.Critical;
            case 'error':
                return LogLevel.Error;
            case 'warning':
                return LogLevel.Warning;
            case 'information':
                return LogLevel.Information;
            case 'debug':
                return LogLevel.Debug;
            case 'trace':
                return LogLevel.Trace;
            default:
                return LogLevel.Information;
        }
    }
}
